import React, { useState } from "react";
import { Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { organizationService } from "../../services/api/organization";

const BulkPostOnboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    count: number;
    organizationIds: string[];
  } | null>(null);

  const handleSendBulk = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await organizationService.sendBulkPostOnboardDocuments();
      if (response && response.status === "success") {
        setResult(response.data);
      } else if (response && typeof response.count === "number") {
        setResult(response);
      }
    } catch (error) {
      console.error("Bulk send failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bulk Send Post-Onboarding Documents
          </h1>
          <p className="text-gray-600">
            Send the updated MSA/SLA Agreement Pack to all completed organizations that haven't signed yet.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <AlertCircle className="text-amber-500 w-5 h-5" />
            Important Information
          </h2>
          <div className="text-gray-600 text-sm space-y-3">
            <p>
              This action will queue the sending of the updated MSA and SLA agreement pack to all active organizations with the onboarding status of <span className="font-bold text-slate-800">COMPLETED</span>.
            </p>
            <p>
              Specifically, this action:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
              <li>Resets any existing pending post-onboard document signatures.</li>
              <li>Updates the agreement status for these organizations to <span className="font-bold text-amber-600">SENT</span>.</li>
              <li>Triggers the email delivery of the new SLA and MSA agreements to the organization representatives.</li>
              <li>Starts automated email reminders to sign the agreements every 2 days.</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          {!result ? (
            <div className="space-y-6">
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                Clicking the button below will perform the batch send operation across the entire platform. This process cannot be undone once started.
              </p>
              <button
                onClick={handleSendBulk}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold px-8 py-3.5 rounded-lg shadow transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending Agreement Packs...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Bulk Post-Onboard Documents
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="w-16 h-16 bg-green-50 border border-green-100 rounded-full flex items-center justify-center mx-auto text-green-500">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Agreement Packs Sent Successfully!
                </h3>
                <p className="text-gray-600 max-w-md mx-auto text-sm">
                  The updated MSA/SLA agreements have been sent to <span className="font-bold text-green-600">{result.count}</span> completed organization(s).
                </p>
              </div>

              {result.organizationIds.length > 0 && (
                <div className="text-left bg-gray-50 rounded-lg p-4 max-w-md mx-auto max-h-48 overflow-y-auto border border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                    Processed Organization IDs:
                  </span>
                  <ul className="space-y-1">
                    {result.organizationIds.map((id) => (
                      <li key={id} className="text-xs font-mono text-gray-600 bg-white px-2 py-1 rounded border border-gray-100">
                        {id}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => setResult(null)}
                className="text-sm font-semibold text-blue-600 hover:text-blue-800 focus:outline-none"
              >
                Reset and Send Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPostOnboard;
